import { FC, useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Dropdown, InputAlignmentContainer, MultiSelectDropdown, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Button, Buttons } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf } from "capitalrx-components-2.0/lib/layout";

import { pricingCompoundOptions, pricingCompoundOptionTypes } from "../../constants";

interface CompoundItemProps {
  index: number;
  onDelete: (id: number) => void;
}

export const CompoundItem: FC<CompoundItemProps> = (props) => {
  const { index, onDelete } = props;

  const form = useFormContext();
  const formValues = form?.getValues();

  const type = useWatch({ name: `compound_pricing.${index}.type` });

  const renderCompoundTypeOptions = pricingCompoundOptions.map((option) => (
    <Dropdown.Option key={option.value} value={option.value}>
      {option.label}
    </Dropdown.Option>
  ));

  const validateTypes = useCallback(
    (value) => {
      const countItemsWithValue = (arr, searchValue) => {
        if (!Array.isArray(arr)) return 0;
        return arr.filter((item) => item.type === searchValue).length;
      };

      const counts = countItemsWithValue(formValues?.compound_pricing, value);
      return counts > 1 ? "Can't select the same type twice" : undefined;
    },
    [formValues],
  );

  const displayValue = (type) => {
    const valueType = pricingCompoundOptionTypes[type];

    if (valueType === "%")
      return <Textbox.Field label="Value" name={`compound_pricing.${index}.value`} testId={`test__value_${index}`} suffix="%" />;
    if (valueType === "$")
      return <Textbox.Field label="Value" name={`compound_pricing.${index}.value`} testId={`test__value_${index}`} suffix="$" />;
    if (valueType === "string")
      return <Textbox.Field label="Value" name={`compound_pricing.${index}.value`} testId={`test__value_${index}`} />;
    if (valueType === "lesser_of_first_of")
      return (
        <Dropdown.Field label="Value" name={`compound_pricing.${index}.value`} testId={`test__value_${index}`}>
          <Dropdown.Option value="lesser_of">Lesser of JUDI selected options</Dropdown.Option>
          <Dropdown.Option value="first_of">
            First of selected JUDI options + compare to submitted pricing to find lesser of
          </Dropdown.Option>
        </Dropdown.Field>
      );
    if (valueType === "selected_price_types")
      return (
        <MultiSelectDropdown.Field label="Value" name={`compound_pricing.${index}.value`} testId={`test__value_${index}`} required>
          <MultiSelectDropdown.Option value="awp">AWP</MultiSelectDropdown.Option>
          <MultiSelectDropdown.Option value="nadac">NADAC</MultiSelectDropdown.Option>
          <MultiSelectDropdown.Option value="wac">WAC</MultiSelectDropdown.Option>
          <MultiSelectDropdown.Option value="specialty_list">Specialty List</MultiSelectDropdown.Option>
          <MultiSelectDropdown.Option value="mac_list">MAC List</MultiSelectDropdown.Option>
          <MultiSelectDropdown.Option value="usual_and_customary">UNC</MultiSelectDropdown.Option>
          <MultiSelectDropdown.Option value="submitted">Submitted Pricing</MultiSelectDropdown.Option>
        </MultiSelectDropdown.Field>
      );

    return null;
  };

  return (
    <Grid>
      <Grid.Span columns={4}>
        <Dropdown.Field
          label="Type"
          name={`compound_pricing.${index}.type`}
          testId={`test__type_${index}`}
          hideInstructions
          searchable
          validator={validateTypes}
        >
          {renderCompoundTypeOptions}
        </Dropdown.Field>
      </Grid.Span>
      <Grid.Span columns={4}>{displayValue(type)}</Grid.Span>
      <Grid.Span columns={1}>
        <InputAlignmentContainer>
          <Shelf>
            <Button appearance={Buttons.Destructive.Tertiary.Large} transparent onClick={() => onDelete(index)}>
              Remove
            </Button>
            <Shelf.Spacer />
          </Shelf>
        </InputAlignmentContainer>
      </Grid.Span>
    </Grid>
  );
};
